import classNames from 'classnames';
import { paddingMinusBorder } from '../utils/tailwind/paddingMinusBorder';
import type { ButtonAccent, ButtonSize, ButtonVariant } from './Button.types';

const baseInteractivityClassName =
  'cursor-pointer focus-visible:outline-none focus-visible:ring';

const enabledBaseVariantClassName: { [Key in ButtonVariant]: string } = {
  primary: `${baseInteractivityClassName}  border border-solid border-shade-10 hover:bg-state-hover active:bg-state-press`,
  outline: `${baseInteractivityClassName}  border border-solid bg-default hover:bg-state-hover active:bg-state-press`,
  'light-outline': `${baseInteractivityClassName}  border border-solid bg-default hover:bg-state-hover active:bg-state-press`,
  plain: `${baseInteractivityClassName} border-none bg-transparent hover:bg-state-hover active:bg-state-press`,
};

const enabledVariantClassName: {
  [VariantKey in ButtonVariant]: {
    [AccentKey in ButtonAccent]: string;
  };
} = {
  primary: {
    positive: `${enabledBaseVariantClassName.primary} text-element-on-primary bg-element-primary`,
    negative: `${enabledBaseVariantClassName.primary} text-status-on-error bg-status-error`,
  },
  outline: {
    positive: `${enabledBaseVariantClassName.outline} text-element-secondary border-element-secondary`,
    negative: `${enabledBaseVariantClassName.outline} text-element-negative border-element-negative`,
  },
  'light-outline': {
    positive: `${enabledBaseVariantClassName.outline} text-default border-default`,
    negative: `${enabledBaseVariantClassName.outline} text-element-negative border-default`,
  },
  plain: {
    positive: `${enabledBaseVariantClassName.plain} text-element-secondary`,
    negative: `${enabledBaseVariantClassName.plain} text-element-negative`,
  },
};

const disabledVariantClassName: { [Key in ButtonVariant]: string } = {
  primary:
    'pointer-events-none text-plain bg-status-disabled-dark border border-solid border-shade-10',
  outline:
    'pointer-events-none text-status-disabled-dark bg-status-disabled border border-status-disabled-dark border-solid',
  'light-outline':
    'pointer-events-none text-status-disabled-dark bg-status-disabled border border-status-disabled-dark border-solid',
  plain: 'pointer-events-none text-status-disabled-dark border-none',
};

const sizeClassName: { [Key in ButtonSize]: string } = {
  small: `${paddingMinusBorder['2xs']} text-xs`,
  default: `${paddingMinusBorder['2xs']} text-sm`,
  large: `${paddingMinusBorder.xs} text-base`,
};

const radiusClassName: { [Key in ButtonSize]: string } = {
  small: 'rounded-sm',
  default: 'rounded',
  large: 'rounded-lg',
};

export const getButtonClassNames = (
  variant: ButtonVariant,
  className?: string,
  accent: ButtonAccent = 'positive',
  size: ButtonSize = 'default',
  disabled?: boolean,
  pressed?: boolean,
): string =>
  classNames(
    className,
    'relative inline-flex shrink-0 items-center justify-center whitespace-nowrap font-semibold no-underline subtract-border',
    disabled
      ? disabledVariantClassName[variant]
      : enabledVariantClassName[variant][accent],
    sizeClassName[size],
    radiusClassName[size],
    pressed && 'bg-state-press',
  );
